/** Named date/time layouts, so call sites say what they show and not how it is spelled. */
export type DateFormatStyle = 'time' | 'timeSeconds' | 'short' | 'medium';

const DATE_FORMAT_OPTIONS: Readonly<Record<DateFormatStyle, Intl.DateTimeFormatOptions>> = {
  time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  timeSeconds: { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
  short: { dateStyle: 'short', timeStyle: 'short' },
  medium: { dateStyle: 'medium', timeStyle: 'short' },
};

const formatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: string, style: DateFormatStyle): Intl.DateTimeFormat {
  const key = `${locale}|${style}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, DATE_FORMAT_OPTIONS[style]);
    formatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Formats a date or time for the given language.
 *
 * @param value A `Date`, an ISO string or epoch milliseconds; anything else yields an empty string.
 * @param locale BCP 47 tag of the active language (`LocaleService.dateLocale()`).
 * @param style Which layout to use; defaults to date and time.
 * @returns The formatted text, or `''` when the value is missing or not a valid date.
 * @example
 * formatDate(new Date('2026-10-05T14:03:00'), 'en', 'time'); // '14:03'
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  locale: string,
  style: DateFormatStyle = 'medium',
): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dateFormatter(locale, style).format(date);
}

/**
 * Formats a number for the given language, with its grouping and decimal separators.
 *
 * @param value The number to format; `null`/`undefined` yield an empty string.
 * @param locale BCP 47 tag of the active language.
 * @param options Optional `Intl.NumberFormat` options.
 * @example
 * formatNumber(1234.5, 'pt'); // '1.234,5'
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(locale, options).format(value);
}
